import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  Users, 
  CheckCircle, 
  Clock, 
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  Mail,
  CreditCard,
  TrendingUp,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';

const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' or 'enrollments'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    fetchPayments();
    fetchEnrollments();
  }, []);

  const fetchPayments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/override/payments?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/override/enrollments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data.enrollments || []);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  // Process a single pending payment
  const processPayment = async (sessionId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this payment?`)) return;
    
    setProcessingPayment(sessionId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/override/process-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          action: action,
          reason: `Admin ${action}d payment manually`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        // Refresh payments
        fetchPayments();
        fetchEnrollments();
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Error processing payment');
    } finally {
      setProcessingPayment(null);
    }
  };

  // Bulk process all pending payments
  const bulkProcessPayments = async () => {
    const pendingCount = payments.filter(p => p.status === 'initiated' || p.status === 'pending').length;
    if (!window.confirm(`This will approve ALL ${pendingCount} pending payments and grant access to users. Continue?`)) return;
    
    setBulkProcessing(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/override/bulk-process-payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Successfully processed ${data.processed?.length || 0} payments!`);
        fetchPayments();
        fetchEnrollments();
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error bulk processing:', error);
      alert('Error processing payments');
    } finally {
      setBulkProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbol = currency?.toUpperCase() === 'GBP' ? '£' : '$';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      initiated: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
      </span>
    );
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.package_id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const filteredEnrollments = enrollments.filter(enrollment => {
    return enrollment.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           enrollment.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/content')}
            className="flex items-center text-purple-600 hover:text-purple-800 mb-4 transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Panel
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="payments-page-title">Payment Management</h1>
          <p className="text-gray-600 mt-1">Track payments and enrollments from mentees</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.total_revenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed Payments</p>
                <p className="text-2xl font-bold text-gray-900">{summary.completed_payments || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Payments</p>
                <p className="text-2xl font-bold text-gray-900">{summary.pending_payments || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Enrollments</p>
                <p className="text-2xl font-bold text-gray-900">{enrollments.filter(e => e.is_active).length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'payments'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <CreditCard className="w-4 h-4 inline mr-2" />
                Payment History
              </button>
              <button
                onClick={() => setActiveTab('enrollments')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'enrollments'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Enrolled Mentees
              </button>
            </nav>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            {activeTab === 'payments' && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="initiated">Initiated/Pending</option>
                  <option value="failed">Failed</option>
                </select>
                
                {/* Bulk Approve Button */}
                {payments.filter(p => p.status === 'initiated' || p.status === 'pending').length > 0 && (
                  <button
                    onClick={bulkProcessPayments}
                    disabled={bulkProcessing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center whitespace-nowrap"
                    data-testid="bulk-approve-btn"
                  >
                    {bulkProcessing ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve All Pending ({payments.filter(p => p.status === 'initiated' || p.status === 'pending').length})
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {activeTab === 'payments' ? (
              filteredPayments.length > 0 ? (
                <div className="space-y-4">
                  {filteredPayments.map((payment, index) => (
                    <div 
                      key={payment.id || index}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedPayment(expandedPayment === index ? null : index)}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{payment.user_name || 'Unknown User'}</p>
                            <p className="text-sm text-gray-500">{payment.user_email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-6">
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{formatCurrency(payment.amount, payment.currency)}</p>
                            <p className="text-xs text-gray-500">{payment.package_id}</p>
                          </div>
                          {getStatusBadge(payment.status)}
                          <div className="text-right text-sm text-gray-500">
                            {formatDate(payment.created_at)}
                          </div>
                          {expandedPayment === index ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedPayment === index && (
                        <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-gray-500">Transaction ID</p>
                              <p className="font-medium">{payment.id || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Session ID</p>
                              <p className="font-medium text-xs truncate">{payment.session_id || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Payment Method</p>
                              <p className="font-medium">{payment.payment_method || 'Stripe'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Course/Track</p>
                              <p className="font-medium">{payment.course_id || payment.track || 'Mentorship'}</p>
                            </div>
                          </div>
                          
                          {/* Admin Actions for Pending Payments */}
                          {(payment.status === 'initiated' || payment.status === 'pending') && (
                            <div className="flex items-center justify-end space-x-3 pt-3 border-t border-gray-200">
                              <span className="text-sm text-gray-500 mr-auto">
                                <AlertCircle className="w-4 h-4 inline mr-1 text-yellow-500" />
                                Payment awaiting confirmation
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); processPayment(payment.session_id, 'reject'); }}
                                disabled={processingPayment === payment.session_id}
                                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                              >
                                Reject
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); processPayment(payment.session_id, 'approve'); }}
                                disabled={processingPayment === payment.session_id}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                              >
                                {processingPayment === payment.session_id ? (
                                  <>
                                    <span className="animate-spin mr-2">⏳</span>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve & Grant Access
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No payments found</p>
                </div>
              )
            ) : (
              filteredEnrollments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                        <th className="pb-3 font-medium">Mentee</th>
                        <th className="pb-3 font-medium">Track</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Total Paid</th>
                        <th className="pb-3 font-medium">Expires</th>
                        <th className="pb-3 font-medium">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredEnrollments.map((enrollment, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-gray-900">{enrollment.name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500">{enrollment.email}</p>
                            </div>
                          </td>
                          <td className="py-4">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                              {enrollment.track || 'Not Set'}
                            </span>
                          </td>
                          <td className="py-4">
                            {enrollment.is_active ? (
                              <span className="flex items-center text-green-600 text-sm">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="flex items-center text-red-600 text-sm">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                Expired
                              </span>
                            )}
                          </td>
                          <td className="py-4 font-medium">
                            {formatCurrency(enrollment.total_paid)}
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {formatDate(enrollment.subscription_expires)?.split(',')[0] || 'N/A'}
                          </td>
                          <td className="py-4">
                            {enrollment.admin_granted ? (
                              <span className="text-xs text-orange-600">Admin Granted</span>
                            ) : (
                              <span className="text-xs text-green-600">Paid</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No enrollments found</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentsPage;
